import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

/** Inactive surface — matches Lil Dragons subject strip in `LilDragonsTab`. */
const LIL_DRAGONS_SUBJECT_TAB_SURFACE = "!bg-white !text-gray-900";

const patternsTabsListClassName =
  "mb-4 flex h-auto w-full flex-wrap gap-2 rounded-full border-0 bg-gray-100 p-1 sm:flex-nowrap";

const patternsTabTriggerClassName = cn(
  "inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full border border-black/10 px-3 py-2.5 text-center text-sm font-medium leading-snug transition-shadow sm:px-4",
  LIL_DRAGONS_SUBJECT_TAB_SURFACE,
  "hover:border-black/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-taekwondo-blue/35 focus-visible:ring-offset-2",
  "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
);

interface LilDragonsPatternsTabContentProps {
  hasTrainingVideo: (aliases: string[]) => boolean;
  handleWatchTrainingVideo: (aliases: string[]) => void;
}

export function LilDragonsPatternsTabContent({
  hasTrainingVideo,
  handleWatchTrainingVideo,
}: LilDragonsPatternsTabContentProps) {
  return (
    <div className="w-full">
      <Tabs defaultValue="daemahngchun" className="w-full">
        <TabsList className={patternsTabsListClassName}>
          <TabsTrigger value="daemahngchun" className={patternsTabTriggerClassName}>
            DaeMahngChun
          </TabsTrigger>
          <TabsTrigger value="taeguek" className={patternsTabTriggerClassName}>
            Taeguek
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="daemahngchun"
          className="mt-0 focus-visible:outline-none"
        >
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-taekwondo-red mb-4">
              DaeMahngChun (DMC)
            </h3>
            <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
              <img
                src="/images/dragons/dragon_fire.PNG"
                alt="Lil' Dragon practising DMC"
                className="h-28 w-auto flex-shrink-0"
              />
              <p className="text-sm text-gray-700 text-left">
                DaeMahngChun (DMC) is one of our special MooGongRyu patterns. It
                helps dragons learn how to move around the floor, use strong
                blocks and punches, and remember longer sequences of moves.
                These patterns get more powerful as you go from DMC 1 to DMC 3,
                and they help build sharp focus and awesome balance.
              </p>
            </div>
            <Tabs defaultValue="dmc1" className="w-full">
              <TabsList className={patternsTabsListClassName}>
                <TabsTrigger value="dmc1" className={patternsTabTriggerClassName}>
                  DMC 1
                </TabsTrigger>
                <TabsTrigger value="dmc2" className={patternsTabTriggerClassName}>
                  DMC 2
                </TabsTrigger>
                <TabsTrigger value="dmc3" className={patternsTabTriggerClassName}>
                  DMC 3
                </TabsTrigger>
              </TabsList>
              <TabsContent value="dmc1">
                <div className="space-y-4 pt-2">
                  {hasTrainingVideo(["daemahng_chun_1"]) && (
                    <div>
                      <button
                        type="button"
                        className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                        onClick={() =>
                          handleWatchTrainingVideo(["daemahng_chun_1"])
                        }
                      >
                        Watch Demonstration Video
                      </button>
                    </div>
                  )}
                  <div className="flex justify-start mb-2">
                    <img
                      src="/images/belts/white_belt.png"
                      alt="White belt"
                      className="h-8 w-auto"
                    />
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      KEY
                    </h4>
                    <div className="flex flex-row items-start justify-between gap-4">
                      <ul className="text-gray-700 space-y-1 text-sm">
                        <li>CS - Cat stance</li>
                        <li>BS - Back stance</li>
                        <li>FS - Front stance</li>
                        <li>TS - Tiger stance</li>
                        <li>KH - Knife Hand</li>
                      </ul>
                      <img
                        src="/images/instructions/dmc_floor_plan.png"
                        alt="DMC Floor Plan"
                        className="h-[100px] w-auto object-contain flex-shrink-0"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      JOONBI
                    </h4>
                    <p className="text-gray-700 text-sm">
                      Arms go up and round, ending in front of the body with
                      hands closed, left over right, crossed in front of the
                      chest.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      MOVES
                    </h4>
                    <ol className="list-none space-y-2 text-sm">
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">1.</span>
                        <span className="text-gray-700">
                          Turn to Left (B) long front stance (FS), down block
                          with front arm (L), then mid punch with R arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">2.</span>
                        <span className="text-gray-700">
                          Turn to Right (C) long FS, down block with front arm
                          (R), then mid punch with L arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">3.</span>
                        <span className="text-gray-700">
                          Step L leg to front towards D, long FS, down block
                          with front arm (L), then mid double punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">4.</span>
                        <span className="text-gray-700">
                          Step R leg forwards towards D, long FS, down block
                          with front arm (R), then mid double punch{" "}
                          <span className="font-semibold">KIHAP</span>.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">5.</span>
                        <span className="text-gray-700">
                          Turn back end round, moving L leg to E, long FS, down
                          block with front arm (L), then mid punch with R arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">6.</span>
                        <span className="text-gray-700">
                          Turn to Right (F), long FS, down block with front arm
                          (R), then mid punch with L arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">7.</span>
                        <span className="text-gray-700">
                          Step L leg to back towards A, long FS, down block with
                          front arm (L), then mid double punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">8.</span>
                        <span className="text-gray-700">
                          Step R leg forwards towards A, long FS, down block
                          with front arm (R), then mid double punch{" "}
                          <span className="font-semibold">KIHAP</span>.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">9.</span>
                        <span className="text-gray-700">
                          Turn back end round, moving L leg to B, long FS, down
                          block with front arm (L), then mid punch with R arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">10.</span>
                        <span className="text-gray-700">
                          Turn to Right (C), long FS, down block with front arm
                          (R), then mid punch with L arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">11.</span>
                        <span className="text-gray-700">
                          Step L leg to front towards D, back stance, slow down
                          knife block, double mid punch, then adjust to long FS
                          with one high punch{" "}
                          <span className="font-semibold">KIHAP</span>.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">12.</span>
                        <span className="text-gray-700">
                          Step back to Joonbi.
                        </span>
                      </li>
                    </ol>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="dmc2">
                <div className="space-y-4 pt-2">
                  {hasTrainingVideo(["daemahng_chun_2"]) && (
                    <div>
                      <button
                        type="button"
                        className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                        onClick={() =>
                          handleWatchTrainingVideo(["daemahng_chun_2"])
                        }
                      >
                        Watch Demonstration Video
                      </button>
                    </div>
                  )}
                  <div className="flex justify-start mb-2">
                    <img
                      src="/images/belts/yellow_belt.png"
                      alt="Yellow belt"
                      className="h-8 w-auto"
                    />
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      KEY
                    </h4>
                    <div className="flex flex-row items-start justify-between gap-4">
                      <ul className="text-gray-700 space-y-1 text-sm">
                        <li>CS - Cat stance</li>
                        <li>BS - Back stance</li>
                        <li>FS - Front stance</li>
                        <li>TS - Tiger stance</li>
                        <li>KH - Knife Hand</li>
                      </ul>
                      <img
                        src="/images/instructions/dmc_floor_plan.png"
                        alt="DMC Floor Plan"
                        className="h-[100px] w-auto object-contain flex-shrink-0"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      JOONBI
                    </h4>
                    <p className="text-gray-700 text-sm">Joonbi.</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      MOVES
                    </h4>
                    <ol className="list-none space-y-2 text-sm">
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">1.</span>
                        <span className="text-gray-700">
                          Turn to Left (B) long front stance (FS), rising block
                          with front arm (L), then high punch with R arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">2.</span>
                        <span className="text-gray-700">
                          Turn to Right (C) long FS, rising block with front arm
                          (R), then high punch with L arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">3.</span>
                        <span className="text-gray-700">
                          Step L leg to front towards D, long FS, rising block
                          with front arm (L), then high double punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">4.</span>
                        <span className="text-gray-700">
                          Step R leg forwards towards D, long FS, rising block
                          with front arm (R), then high double punch{" "}
                          <span className="font-semibold">KIHAP</span>.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">5.</span>
                        <span className="text-gray-700">
                          Turn back end round, moving L leg to E, long FS,
                          rising block with front arm (L), then high punch with
                          R arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">6.</span>
                        <span className="text-gray-700">
                          Turn to Right (F), long FS, rising block with front
                          arm (R), then high punch with L arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">7.</span>
                        <span className="text-gray-700">
                          Step L leg to back towards A, long FS, rising block
                          with front arm (L), then high double punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">8.</span>
                        <span className="text-gray-700">
                          Step R leg forwards towards A, long FS, rising block
                          with front arm (R), then double high punch{" "}
                          <span className="font-semibold">KIHAP</span>.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">9.</span>
                        <span className="text-gray-700">
                          Turn back end round, moving L leg to B, long FS,
                          rising block with front arm (L), then high punch with
                          R arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">10.</span>
                        <span className="text-gray-700">
                          Turn to Right (C), long FS, rising block with front
                          arm (R), then high punch with L arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">11.</span>
                        <span className="text-gray-700">
                          Step L leg to front towards D, back stance, slow
                          rising knife block, two high punches, adjust to long
                          FS and one high punch{" "}
                          <span className="font-semibold">KIHAP</span>.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">12.</span>
                        <span className="text-gray-700">
                          Step back to Joonbi.
                        </span>
                      </li>
                    </ol>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="dmc3">
                <div className="space-y-4 pt-2">
                  {hasTrainingVideo(["daemahng_chun_3"]) && (
                    <div>
                      <button
                        type="button"
                        className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                        onClick={() =>
                          handleWatchTrainingVideo(["daemahng_chun_3"])
                        }
                      >
                        Watch Demonstration Video
                      </button>
                    </div>
                  )}
                  <div className="flex justify-start mb-2">
                    <img
                      src="/images/belts/orange_belt.png"
                      alt="Orange belt"
                      className="h-8 w-auto"
                    />
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      KEY
                    </h4>
                    <div className="flex flex-row items-start justify-between gap-4">
                      <ul className="text-gray-700 space-y-1 text-sm">
                        <li>CS - Cat stance</li>
                        <li>BS - Back stance</li>
                        <li>FS - Front stance</li>
                        <li>TS - Tiger stance</li>
                        <li>KH - Knife Hand</li>
                      </ul>
                      <img
                        src="/images/instructions/dmc_floor_plan.png"
                        alt="DMC Floor Plan"
                        className="h-[100px] w-auto object-contain flex-shrink-0"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      JOONBI
                    </h4>
                    <p className="text-gray-700 text-sm">
                      Joonbi &ndash; turn fist outwards.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      MOVES
                    </h4>
                    <ol className="list-none space-y-2 text-sm">
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">1.</span>
                        <span className="text-gray-700">
                          Turn to Left (B), cat stance (CS), inner forearm block
                          with front arm (L), then mid punch with R arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">2.</span>
                        <span className="text-gray-700">
                          Turn to Right (C), CS, inner forearm block with front
                          arm (R), then mid punch with L arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">3.</span>
                        <span className="text-gray-700">
                          Step L leg to front towards D, back stance (BS), inner
                          forearm block with front arm (L), then mid-to-high
                          punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">4.</span>
                        <span className="text-gray-700">
                          Step R leg forwards towards D, BS, inner forearm block
                          with front arm (R), then mid-to-high punch{" "}
                          <span className="font-semibold">KIHAP</span>.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">5.</span>
                        <span className="text-gray-700">
                          Turn back end round, moving L leg to E, cat stance
                          (CS), inner forearm block with front arm (L), then mid
                          punch with R arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">6.</span>
                        <span className="text-gray-700">
                          Turn to Right (F), CS, inner forearm block with front
                          arm (R), then mid punch with L arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">7.</span>
                        <span className="text-gray-700">
                          Step L leg to back towards A, BS, inner forearm block
                          with front arm (L), then mid-to-high punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">8.</span>
                        <span className="text-gray-700">
                          Step R leg forwards towards A, BS, inner forearm block
                          with front arm (R), then mid-to-high punch{" "}
                          <span className="font-semibold">KIHAP</span>.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">9.</span>
                        <span className="text-gray-700">
                          Turn back end round, moving L leg to B, CS, inner
                          forearm block with front arm (L), then mid punch with
                          R arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">10.</span>
                        <span className="text-gray-700">
                          Turn to Right (C), CS, inner forearm block with front
                          arm (R), then mid punch with L arm.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">11.</span>
                        <span className="text-gray-700">
                          Step L leg to front towards D, back stance, slow inner
                          forearm reverse ridge block (open hand), mid-to-high,
                          adjust to long FS with one high punch{" "}
                          <span className="font-semibold">KIHAP</span>.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">12.</span>
                        <span className="text-gray-700">
                          Step back to Joonbi.
                        </span>
                      </li>
                    </ol>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </TabsContent>
        <TabsContent
          value="taeguek"
          className="mt-0 focus-visible:outline-none"
        >
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
              <img
                src="/images/dragons/dragon_fire.PNG"
                alt="Lil' Dragon practising Taegeuk"
                className="h-28 w-auto flex-shrink-0"
              />
              <div className="text-left">
                <h3 className="text-lg font-semibold text-taekwondo-red mb-2">
                  Taeguek 1 &amp; 2
                </h3>
                <p className="text-sm text-gray-700">
                  Taegeuk forms are World Taekwondo patterns that teach balance,
                  power, and flowing movement. <br />
                  <br />
                  Taegeuk 1 and 2 are the first two patterns most dragons learn,
                  helping them build strong basics and confidence.
                </p>
              </div>
            </div>
            <Tabs defaultValue="taeguek1" className="w-full">
              <TabsList className={patternsTabsListClassName}>
                <TabsTrigger value="taeguek1" className={patternsTabTriggerClassName}>
                  Taegeuk 1
                </TabsTrigger>
                <TabsTrigger value="taeguek2" className={patternsTabTriggerClassName}>
                  Taegeuk 2
                </TabsTrigger>
              </TabsList>
              <TabsContent value="taeguek1">
                <div className="space-y-4 pt-2">
                  {hasTrainingVideo(["taeguek_1", "taegeuk_1", "taeguk_1"]) && (
                    <button
                      type="button"
                      className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                      onClick={() =>
                        handleWatchTrainingVideo([
                          "taeguek_1",
                          "taegeuk_1",
                          "taeguk_1",
                        ])
                      }
                    >
                      Watch Demonstration Video
                    </button>
                  )}
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      KEY
                    </h4>
                    <div className="flex flex-row items-start justify-between gap-4">
                      <ul className="text-gray-700 space-y-1 text-sm">
                        <li>CS - Cat stance</li>
                        <li>BS - Back stance</li>
                        <li>FS - Front stance</li>
                        <li>TS - Tiger stance</li>
                        <li>KH - Knife Hand</li>
                      </ul>
                      <img
                        src="/images/instructions/dmc_floor_plan.png"
                        alt="Floor plan"
                        className="h-[100px] w-auto object-contain flex-shrink-0"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      ABOUT
                    </h4>
                    <p className="text-gray-700 text-sm">
                      Taegeuk Il Jang represents the symbol of &quot;Keon&quot;,
                      one of the 8 Kwaes (divination signs), which means the
                      heaven and yang. As Keon symbolizes the beginning of the
                      creation of all things in the universe, Taegeuk 1 Jang is
                      the beginning of Taekwondo training.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      MOVES
                    </h4>
                    <ol className="list-none space-y-2 text-sm">
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">1.</span>
                        <span className="text-gray-700">
                          Start in the Ready or Joon Bi stance.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">2.</span>
                        <span className="text-gray-700">
                          Turn left 90 degrees into a walking stance with left
                          foot forward, left low block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">3.</span>
                        <span className="text-gray-700">
                          Step forward into walking stance with right foot
                          forward, right middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">4.</span>
                        <span className="text-gray-700">
                          Turn right 180 degrees into a walking stance with
                          right foot forward, right low block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">5.</span>
                        <span className="text-gray-700">
                          Step forward into walking stance with left foot
                          forward, left middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">6.</span>
                        <span className="text-gray-700">
                          Turn left 90 degrees into a front stance with left
                          foot forward, left low block then right middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">7.</span>
                        <span className="text-gray-700">
                          Turn right 90 degrees into a walking stance with right
                          foot forward, left inside block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">8.</span>
                        <span className="text-gray-700">
                          Step forward into walking stance with left foot
                          forward, right middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">9.</span>
                        <span className="text-gray-700">
                          Turn left 180 degrees into a walking stance with left
                          foot forward, right inside block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">10.</span>
                        <span className="text-gray-700">
                          Step forward into walking stance with right foot
                          forward, left middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">11.</span>
                        <span className="text-gray-700">
                          Turn right 90 degrees into a front stance with right
                          foot forward, right low block then left middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">12.</span>
                        <span className="text-gray-700">
                          Turn left 90 degrees into a walking stance with left
                          foot forward, left high block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">13.</span>
                        <span className="text-gray-700">
                          Right front kick, landing in walking stance with right
                          foot forward, right middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">14.</span>
                        <span className="text-gray-700">
                          Turn right 180 degrees into a walking stance with
                          right foot forward, right high block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">15.</span>
                        <span className="text-gray-700">
                          Left front kick, landing in walking stance with left
                          foot forward, left middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">16.</span>
                        <span className="text-gray-700">
                          Turn right 90 degrees into a front stance with left
                          foot forward, left low block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">17.</span>
                        <span className="text-gray-700">
                          Step forward into front stance with right foot
                          forward, right middle punch{" "}
                          <span className="font-semibold">KIHAP</span>.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">18.</span>
                        <span className="text-gray-700">
                          When the Master says &quot;Bah Ro&quot; (return), turn
                          and face them.
                        </span>
                      </li>
                    </ol>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="taeguek2">
                <div className="space-y-4 pt-2">
                  {hasTrainingVideo(["taeguek_2", "taegeuk_2", "taeguk_2"]) && (
                    <button
                      type="button"
                      className="inline-flex items-center text-sm font-medium text-taekwondo-blue hover:underline"
                      onClick={() =>
                        handleWatchTrainingVideo([
                          "taeguek_2",
                          "taegeuk_2",
                          "taeguk_2",
                        ])
                      }
                    >
                      Watch Demonstration Video
                    </button>
                  )}
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      KEY
                    </h4>
                    <div className="flex flex-row items-start justify-between gap-4">
                      <ul className="text-gray-700 space-y-1 text-sm">
                        <li>CS - Cat stance</li>
                        <li>BS - Back stance</li>
                        <li>FS - Front stance</li>
                        <li>TS - Tiger stance</li>
                        <li>KH - Knife Hand</li>
                      </ul>
                      <img
                        src="/images/instructions/dmc_floor_plan.png"
                        alt="Floor plan"
                        className="h-[100px] w-auto object-contain flex-shrink-0"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      ABOUT
                    </h4>
                    <p className="text-gray-700 text-sm">
                      Taegeuk Ee Jang symbolizes &quot;Tae&quot;, one of the 8
                      divination signs, which means inner firmness with outer
                      softness. This second form builds on Taegeuk 1 with more
                      flowing stepping and kicks.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-base mb-2 text-taekwondo-red">
                      MOVES
                    </h4>
                    <ol className="list-none space-y-2 text-sm">
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">1.</span>
                        <span className="text-gray-700">
                          Start in the Ready or Joon Bi stance.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">2.</span>
                        <span className="text-gray-700">
                          Turn 90 degrees to the left into walking stance with
                          left foot forward, left low block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">3.</span>
                        <span className="text-gray-700">
                          Step forward into front stance with right foot
                          forward, right middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">4.</span>
                        <span className="text-gray-700">
                          Turn 180 degrees to the right into walking stance with
                          right foot forward, right low block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">5.</span>
                        <span className="text-gray-700">
                          Step forward into front stance with left foot forward,
                          left middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">6.</span>
                        <span className="text-gray-700">
                          Turn 90 degrees to the left into walking stance with
                          left foot forward, right inner middle block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">7.</span>
                        <span className="text-gray-700">
                          Step forward into walking stance with right foot
                          forward, left inner middle block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">8.</span>
                        <span className="text-gray-700">
                          Turn 90 degrees to the left into walking stance with
                          left foot forward, left low block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">9.</span>
                        <span className="text-gray-700">
                          Right front snap kick, landing in front stance with
                          right foot forward, right high punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">10.</span>
                        <span className="text-gray-700">
                          Turn 180 degrees to the right into walking stance with
                          right foot forward, right low block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">11.</span>
                        <span className="text-gray-700">
                          Left front snap kick, landing in front stance with
                          left foot forward, left high punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">12.</span>
                        <span className="text-gray-700">
                          Turn 90 degrees to the left into walking stance with
                          left foot forward, left rising block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">13.</span>
                        <span className="text-gray-700">
                          Step forward into walking stance with right foot
                          forward, right rising block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">14.</span>
                        <span className="text-gray-700">
                          Turn 270 degrees to the left into walking stance with
                          left foot forward, right inner middle block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">15.</span>
                        <span className="text-gray-700">
                          Turn 180 degrees to the right into walking stance with
                          right foot forward, left inner middle block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">16.</span>
                        <span className="text-gray-700">
                          Turn 90 degrees to the left into walking stance with
                          left foot forward, left low block.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">17.</span>
                        <span className="text-gray-700">
                          Right front snap kick, landing in walking stance with
                          right foot forward, right middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">18.</span>
                        <span className="text-gray-700">
                          Left front snap kick, landing in walking stance with
                          left foot forward, left middle punch.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">19.</span>
                        <span className="text-gray-700">
                          Right front snap kick, landing in walking stance with
                          right foot forward, right middle punch{" "}
                          <span className="font-semibold">KIHAP</span>.
                        </span>
                      </li>
                      <li className="flex">
                        <span className="text-taekwondo-red mr-2">20.</span>
                        <span className="text-gray-700">
                          When the Master says &quot;Bah Ro&quot;, turn and face
                          the Master.
                        </span>
                      </li>
                    </ol>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
